/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, debounce, List, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { DebounceTime } from '../../../../Shared/Constants/App';

import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';
import { SearchInput } from '../../../../Shared/Components/Layout/styles';
import { FilterOptions } from './FilterDropDownOptions.styles';
import { dropdownStyles } from '../../../../Shared/Components/Common/MultiSelectDropdownWithCheckboxes/MultiSelectDropdownWithCheckboxes.Style';
import { configureColumnStyles } from '../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import { ApplyFilterWrapper, filterStyles, FilterWrapper, LabelsWrapper, SearchFieldWrapper } from './AllJobs.Style';

interface DropdownOption {
  label: string;
  value: string;
}

interface FilterDropDownOptionsProps {
  options: DropdownOption[];
  handleFilterChange: (values: string[], event?: React.MouseEvent<HTMLInputElement>) => void;
  filterLoader: boolean;
  selectedFilter?: string[];
}

const FilterDropDownOptions: React.FC<FilterDropDownOptionsProps> = ({
  options,
  handleFilterChange,
  filterLoader,
  selectedFilter = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState<string[]>(selectedFilter);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { t } = useTranslation(['oneTimeJob', 'altTexts', 'jobManagement']);

  const { checkBox } = dropdownStyles;
  const { columnsVisible } = configureColumnStyles;
  const { labelStyles, loaderStyles } = filterStyles;

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, DebounceTime),
    []
  );

  const handleToggle = useCallback((value: string) => {
    setSelectedValues((prev) => (prev?.includes(value) ? prev?.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredOptions(options);
  };

  const handleApplyFilters = () => {
    if (filterLoader) return;
    handleFilterChange(selectedValues);
  };

  const handleClearAll = () => {
    setSelectedValues([]);
  };

  useEffect(() => {
    setFilteredOptions(
      options?.filter((option) => option?.label?.toLowerCase()?.includes(debouncedSearch?.toLowerCase()))
    );
  }, [debouncedSearch, options]);

  return (
    <FilterWrapper>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SearchFieldWrapper>
            <SearchInput
              placeholder={t('oneTimeJob:search')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e?.target?.value);
                handleSearchChange(e?.target?.value);
              }}
              sx={{
                flex: 1,
              }}
            />

            {searchTerm && (
              <img
                src={Icons.CancelIcon}
                alt={t('altTexts:clear')}
                onClick={handleClearSearch}
                style={{ cursor: 'pointer' }}
              />
            )}
            <img src={Icons.SearchIcon} alt={t('altTexts:search')} />
          </SearchFieldWrapper>
        </Box>
        <Box>
          {selectedValues?.length > 0 && (
            <LabelsWrapper>
              {selectedValues?.map((val) => {
                const label = options?.find((o) => o?.value === val)?.label || val;
                return (
                  <Box
                    key={val}
                    sx={{
                      ...labelStyles,
                    }}>
                    {label}
                    <img
                      src={Icons.CancelIcon}
                      alt="remove"
                      style={{ width: 14, height: 14, marginLeft: 6, cursor: 'pointer' }}
                      onClick={() => handleToggle(val)}
                    />
                  </Box>
                );
              })}
            </LabelsWrapper>
          )}
        </Box>
      </Box>
      <List
        sx={{
          overflowY: 'auto',
          maxHeight: '65vh',
        }}>
        {filterLoader ? (
          <Box
            sx={{
              ...loaderStyles,
            }}>
            <CircularProgress size={24} style={{ color: theme.palette.primary.dark }} />
          </Box>
        ) : filteredOptions?.length > 0 ? (
          filteredOptions?.map((option: DropdownOption) => (
            <FilterOptions
              key={option?.value}
              selected={selectedValues?.includes(option?.value)}
              onClick={() => handleToggle(option?.value)}>
              {selectedValues?.includes(option?.value) ? (
                <img src={Icons.CheckedIcon} alt={t('altTexts:checked')} style={{ ...checkBox }} />
              ) : (
                <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} style={{ ...checkBox }} />
              )}
              {option?.label}
            </FilterOptions>
          ))
        ) : (
          <Box
            sx={{
              padding: '1rem',
              fontSize: theme.typography.h4?.fontSize,
            }}>
            <Typography color="textSecondary" align="center">
              {t('jobManagement:noOptionsFound')}
            </Typography>
          </Box>
        )}
      </List>
      <ApplyFilterWrapper>
        <Typography
          style={{
            ...columnsVisible,
            opacity: isEmpty(selectedValues) ? 0.5 : 1,
            cursor: isEmpty(selectedValues) ? 'not-allowed' : 'pointer',
          }}
          onClick={handleClearAll}>
          {t('jobManagement:clearAll')}
        </Typography>

        <Typography
          onClick={handleApplyFilters}
          style={{
            ...columnsVisible,
            opacity: filterLoader ? 0.5 : 1,
            cursor: filterLoader ? 'not-allowed' : 'pointer',
          }}>
          {t('jobManagement:applyFilters')}
        </Typography>
      </ApplyFilterWrapper>
    </FilterWrapper>
  );
};

export default React.memo(FilterDropDownOptions);
